import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Trophy, ArrowRight, Check } from 'lucide-react';
import { useChallenges, useCreateChallenge, useActivateChallenge, useChallengeTiers } from '@/hooks/use-challenges';
import { useStrategies } from '@/hooks/use-strategies';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { ChallengeTier } from '@/types/api';

export default function ChallengeNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedTier = searchParams.get('tier') as ChallengeTier | null;

  const [step, setStep] = useState<'tier' | 'strategy' | 'confirm'>(preselectedTier ? 'strategy' : 'tier');
  const [selectedTier, setSelectedTier] = useState<ChallengeTier | null>(preselectedTier || null);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string>('');

  const { data: tiers, isLoading: tiersLoading } = useChallengeTiers();
  const { data: strategies, isLoading: strategiesLoading } = useStrategies();
  const { data: challenges } = useChallenges();
  const createChallenge = useCreateChallenge();
  const activateChallenge = useActivateChallenge();

  // If tier was preselected via URL, jump to strategy step
  useEffect(() => {
    if (preselectedTier && (preselectedTier === 'STARTER' || preselectedTier === 'PRO')) {
      setSelectedTier(preselectedTier);
      setStep('strategy');
    }
  }, [preselectedTier]);

  const tierConfig = selectedTier ? tiers?.[selectedTier] : undefined;

  // Strategies already used in active/pending challenges (bot won't be reused)
  const usedStrategyIds = new Set(
    (challenges ?? [])
      .filter((c) => c.status === 'ACTIVE' || c.status === 'PENDING')
      .map((c) => c.bot?.template?.id)
      .filter(Boolean),
  );

  const publishedStrategies = (strategies ?? []).filter(
    (s) => s.status === 'PUBLISHED' && !usedStrategyIds.has(s.id),
  );
  const allPublished = (strategies ?? []).filter((s) => s.status === 'PUBLISHED');
  const excludedCount = allPublished.length - publishedStrategies.length;

  async function handleCreateAndActivate() {
    try {
      // Step 1: Create challenge
      const challenge = await createChallenge.mutateAsync(selectedTier!);

      // Step 2: Activate with selected strategy
      await activateChallenge.mutateAsync({
        challengeId: challenge.id,
        strategyId: selectedStrategyId,
      });

      navigate(`/challenges/${challenge.id}`);
    } catch {
      // Error is handled by mutation hooks
    }
  }

  if (tiersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-extrabold tracking-tight">New Challenge</h1>
        <p className="text-sm text-ink-3 mt-0.5">
          Pick a tier, link a strategy, and start proving your edge
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-3 text-xs">
        {['tier', 'strategy', 'confirm'].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step === s
                  ? 'bg-orange text-white'
                  : ['tier', 'strategy', 'confirm'].indexOf(step) > i
                    ? 'bg-green/20 text-green'
                    : 'bg-surface text-ink-4'
              }`}
            >
              {['tier', 'strategy', 'confirm'].indexOf(step) > i ? (
                <Check size={12} />
              ) : (
                i + 1
              )}
            </div>
            <span className={step === s ? 'text-ink font-semibold' : 'text-ink-4'}>
              {s === 'tier' ? 'Choose Tier' : s === 'strategy' ? 'Select Strategy' : 'Confirm'}
            </span>
            {i < 2 && <ArrowRight size={12} className="text-ink-4" />}
          </div>
        ))}
      </div>

      {/* STEP 1: Tier Selection */}
      {step === 'tier' && tiers && publishedStrategies.length === 0 && !strategiesLoading && (
        <div className="glass rounded-xl p-6 text-center">
          <p className="text-sm text-ink-3 mb-2">
            {allPublished.length > 0
              ? 'All your published strategies are already in active challenges.'
              : 'You need at least one published strategy to start a challenge.'}
          </p>
          <p className="text-xs text-ink-4 mb-3">
            {allPublished.length > 0
              ? 'Complete or cancel an existing challenge to free up a strategy, or publish a new one.'
              : 'Create a strategy, backtest it, and publish to lock in the rules.'}
          </p>
          <Button size="sm" onClick={() => navigate(allPublished.length > 0 ? '/challenges' : '/strategies/new')}>
            {allPublished.length > 0 ? 'View Challenges' : 'Create a Strategy'}
          </Button>
        </div>
      )}
      {step === 'tier' && tiers && (publishedStrategies.length > 0 || strategiesLoading) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(['STARTER', 'PRO'] as ChallengeTier[]).map((tier) => {
            const config = tiers[tier];
            const isSelected = selectedTier === tier;
            return (
              <button
                key={tier}
                onClick={() => {
                  setSelectedTier(tier);
                  setStep('strategy');
                }}
                className={`glass rounded-xl p-6 text-left transition-all duration-200 cursor-pointer ${
                  isSelected ? 'border-orange bg-orange/10' : 'hover:border-orange/20 hover:bg-orange/5'
                }`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Trophy size={18} className={tier === 'PRO' ? 'text-orange' : 'text-green'} />
                  <span className="text-base font-extrabold">{tier} Challenge</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-ink-4">Capital</div>
                    <div className="font-bold">${config.starting_capital.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-ink-4">Profit Target</div>
                    <div className="font-bold">{config.profit_target_pct}%</div>
                  </div>
                  <div>
                    <div className="text-ink-4">Max Drawdown</div>
                    <div className="font-bold">{config.max_drawdown_pct}%</div>
                  </div>
                  <div>
                    <div className="text-ink-4">Duration</div>
                    <div className="font-bold">{config.duration_days} days</div>
                  </div>
                  <div>
                    <div className="text-ink-4">Min Trades</div>
                    <div className="font-bold">{config.min_trades}</div>
                  </div>
                  <div>
                    <div className="text-ink-4">Reward</div>
                    <div className="font-bold text-orange">${config.reward_amount}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* STEP 2: Strategy Selection */}
      {step === 'strategy' && (
        <div className="space-y-4">
          <div className="glass rounded-xl p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-bold">Selected Tier: {selectedTier}</span>
              <button onClick={() => setStep('tier')} className="text-xs text-orange cursor-pointer">
                Change
              </button>
            </div>
            {tierConfig && (
              <div className="text-xs text-ink-3">
                ${tierConfig.starting_capital.toLocaleString()} capital · {tierConfig.profit_target_pct}% target · {tierConfig.duration_days} days · ${tierConfig.reward_amount} reward
              </div>
            )}
          </div>

          <h2 className="text-sm font-bold">Select a strategy for your challenge bot</h2>

          <p className="text-[11px] text-ink-4">Only published strategies can be used in challenges — rules are locked for the duration.</p>
          {excludedCount > 0 && (
            <p className="text-[11px] text-ink-4 mt-1">
              {excludedCount} strateg{excludedCount === 1 ? 'y' : 'ies'} already in use by active challenges.
            </p>
          )}

          {strategiesLoading ? (
            <Spinner />
          ) : publishedStrategies.length === 0 ? (
            <div className="glass rounded-xl p-6 text-center">
              <p className="text-sm text-ink-3 mb-2">
                You need at least one published strategy to start a challenge.
              </p>
              <p className="text-xs text-ink-4 mb-3">
                Create a strategy, backtest it, and publish to lock in the rules.
              </p>
              <Button size="sm" onClick={() => navigate('/strategies/new')}>
                Create a Strategy
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {publishedStrategies.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setSelectedStrategyId(s.id);
                    setStep('confirm');
                  }}
                  className={`w-full glass rounded-lg p-4 text-left transition-all duration-150 cursor-pointer ${
                    selectedStrategyId === s.id ? 'border-orange/40' : 'hover:border-orange/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold">{s.name}</div>
                      <div className="text-xs text-ink-3 mt-0.5">
                        {s.symbol} · {s.strategy_type}
                        {s.latest_version > 0 && ` · v${s.latest_version}`}
                      </div>
                    </div>
                    <ArrowRight size={14} className="text-ink-4" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP 3: Confirm */}
      {step === 'confirm' && (
        <div className="space-y-4">
          <div className="glass rounded-xl p-5 space-y-3">
            <h2 className="text-sm font-bold mb-3">Confirm Your Challenge</h2>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-lg p-3 bg-surface">
                <div className="text-ink-4 mb-1">Tier</div>
                <div className="font-bold">{selectedTier}</div>
              </div>
              <div className="rounded-lg p-3 bg-surface">
                <div className="text-ink-4 mb-1">Capital</div>
                <div className="font-bold">${tierConfig?.starting_capital.toLocaleString()}</div>
              </div>
              <div className="rounded-lg p-3 bg-surface">
                <div className="text-ink-4 mb-1">Profit Target</div>
                <div className="font-bold">{tierConfig?.profit_target_pct}%</div>
              </div>
              <div className="rounded-lg p-3 bg-surface">
                <div className="text-ink-4 mb-1">Max Drawdown</div>
                <div className="font-bold">{tierConfig?.max_drawdown_pct}%</div>
              </div>
              <div className="rounded-lg p-3 bg-surface">
                <div className="text-ink-4 mb-1">Duration</div>
                <div className="font-bold">{tierConfig?.duration_days} days</div>
              </div>
              <div className="rounded-lg p-3 bg-surface">
                <div className="text-ink-4 mb-1">Reward</div>
                <div className="font-bold text-orange">${tierConfig?.reward_amount}</div>
              </div>
            </div>

            <div className="rounded-lg p-3 bg-surface">
              <div className="text-[10px] text-ink-4 mb-1">Strategy</div>
              <div className="text-xs font-bold">
                {publishedStrategies.find((s) => s.id === selectedStrategyId)?.name || 'Unknown'}
              </div>
            </div>

            {/* What gets activated */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(249,115,22,0.05)', border: '1px solid rgba(249,115,22,0.15)' }}>
              <div className="text-[11px] font-bold mb-2 text-orange">What happens when you activate</div>
              <ul className="text-[11px] text-ink-3 leading-relaxed space-y-1">
                <li>A <strong className="text-ink">Challenge bot</strong> is created in <strong className="text-ink">paper trading mode</strong> (no real money at risk)</li>
                <li>Virtual capital of <strong className="text-ink">${tierConfig?.starting_capital.toLocaleString()}</strong> is assigned to the bot</li>
                <li>Strategy: <strong className="text-ink">{publishedStrategies.find((s) => s.id === selectedStrategyId)?.name}</strong> — rules are locked for the duration</li>
                <li>The <strong className="text-ink">{tierConfig?.duration_days}-day clock</strong> starts immediately</li>
                <li>You cannot modify the strategy or stop the bot without <strong className="text-red">failing</strong> the challenge</li>
                <li>All trades, drawdown, and metrics are tracked automatically in real time</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="ghost" size="sm" onClick={() => setStep('strategy')}>
              Back
            </Button>
            <Button
              size="sm"
              onClick={handleCreateAndActivate}
              disabled={createChallenge.isPending || activateChallenge.isPending}
            >
              {createChallenge.isPending || activateChallenge.isPending ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                <Trophy size={14} className="mr-1.5" />
              )}
              Activate Challenge
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
